<script setup lang="ts">
const route = useRoute()
const { defineOgMeta } = useSeoAttributes()
const { formatDate } = useDate()
const lp = useLocalePath()
const config = useRuntimeConfig()
const {
  public: { baseUrl },
} = useRuntimeConfig()

const articles = useArticles()
const slug = route.params.slug as string
const { data: article } = await useAsyncData(`article-${slug}`, () => articles.fetchBySlug(slug))

if (!article.value) {
  throw createError({
    statusCode: 404,
  })
}

useHead(() => ({
  title: article.value?.title,
  meta: defineOgMeta({
    title: article.value?.title as string,
    description: article.value?.description as string,
    image: article.value?.image,
  }),
  link: [{ rel: 'canonical', href: `${baseUrl}/blog/${slug}` }],
}))

useSchemaOrg([
  defineArticle({
    headline: article.value?.title,
    thumbnailUrl: article.value?.image?.url as string,
    url: config.public.baseUrl + lp(`/blog/${article.value?.slug}`),
    image: article.value?.image?.url as string,
    datePublished: article.value?.publishedAt,
    author() {
      return article.value?.authors.length
        ? {
            name: article.value?.authors[0].name,
            image: article.value?.authors[0].image.url,
          }
        : undefined
    },
  }),
])
</script>

<template>
  <main>
    <Container v-if="article" class="screen-content relative grid grid-cols-4 flex-col px-2 py-32 lg:gap-8 lg:px-0">
      <div class="col-span-full mb-8 flex items-start pl-6 md:pl-12 lg:col-span-1 lg:mt-3 lg:pl-16">
        <BackLink to="/blog">
          {{ $t('blog.back') }}
        </BackLink>
      </div>

      <header
        id="header"
        class="col-span-4 flex flex-col items-start gap-y-2 text-left md:px-12 lg:col-span-3 lg:pl-0 lg:pr-16"
      >
        <Heading as="h1" size="large">
          {{ article.title }}
        </Heading>
        <Paragraph size="large" class="mb-4">
          {{ article.description }}
        </Paragraph>
        <Paragraph class="!text-gray-600">
          <time class="flex items-center gap-1">
            <div v-if="article.category" class="flex items-center gap-1">
              <span class="text-main-500 font-medium">{{ article.category.name }} </span>
              <span>—</span>
            </div>
            {{ formatDate(article?.publishedAt as Date) }}
          </time>
        </Paragraph>
      </header>

      <aside class="col-span-full flex w-full flex-col rounded-lg p-6 md:px-12 lg:col-span-1 lg:pl-16 lg:pr-0">
        <div v-for="author of article.authors || []" :key="author.id" class="flex gap-x-4">
          <NuxtImg
            :src="author.image.url"
            :alt="author.image.alternativeText"
            :width="128"
            :height="128"
            fit="cover"
            class="bordershadow-gray h-14 w-14 rounded-full object-cover"
          />
          <div class="flex flex-col justify-center gap-y-1">
            <Paragraph weight="bold">{{ author.name }}</Paragraph>
            <Paragraph>{{ author.role }}</Paragraph>
          </div>
        </div>
      </aside>

      <article class="col-span-4 lg:col-span-3">
        <div v-if="article.show_image">
          <NuxtImg
            :src="article.image.url"
            :alt="article.title"
            :width="1200"
            :height="600"
            fit="cover"
            class="bordershadow-lg-gray aspect-video h-full w-full rounded-lg object-cover"
          />
        </div>

        <main class="w-full px-6 pb-16 md:px-12 lg:pl-0 lg:pr-16" :class="[{ 'pt-16': article.show_image }]">
          <RichText :raw="article.content" />
        </main>
      </article>
    </Container>

    <LazyCta :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
